// Диалог просмотра документа - на базе общего DocumentLayout

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  Grid,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  History,
  Inventory2,
  QrCode2,
  ContentCopy,
  CheckCircle,
  Warning,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  LocalShipping as ShippingIcon,
  Store as StoreIcon,
  PendingActions as PendingIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { Document, DocumentTableRowData } from './types';
import { isErrorStatus, formatDate, getDocumentNumber, getStatusText } from './utils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DocumentLayout, { colors } from './DocumentLayout';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';



const getStatusColor = (status: number): string => {
  switch (status) {
    case 1: return colors.info;
    case 2: return colors.primary;
    case 3: return colors.success;
    case 4: return colors.success;
    case 5: return colors.error;
    case 6: return colors.secondary;
    case 7: return colors.info;
    case 9: return colors.warning;
    case 10: return colors.success;
    case 11: return colors.error;
    case 12: return colors.warning;
    case 13: return colors.warning;
    case 14: return colors.error;
    case 15: return colors.success;
    case 16: return colors.secondary;
    default: return '#94a3b8';
  }
};

// Кнопка копирования
const CopyButton: React.FC<{ text: string | number }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(text));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!text) return null;

  return (
    <Tooltip title={copied ? "Скопировано!" : "Копировать"} arrow>
      <IconButton onClick={handleCopy} size="small" sx={{ p: 0.5, ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
        {copied ? <CheckCircle sx={{ fontSize: 14, color: colors.success }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
      </IconButton>
    </Tooltip>
  );
};

// Компонент поля
const Field: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode; fullWidth?: boolean; copyable?: boolean }> = ({ label, value, icon, fullWidth, copyable }) => {
  if (!value || value === '-' || value === '') return null;
  return (
    <Box sx={{ mb: 2, minWidth: fullWidth ? '100%' : 'auto' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 500 }}>
        {icon && <Box component="span" sx={{ opacity: 0.7, transform: 'scale(0.8)', display: 'flex' }}>{icon}</Box>}
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}>
          {value}
        </Typography>
        {copyable && (typeof value === 'string' || typeof value === 'number') && <CopyButton text={value} />}
      </Box>
    </Box>
  );
};

interface DocumentViewDialogProps {
  open: boolean;
  document: Document | null;
  loading?: boolean;
  onClose: () => void;
  onCreateFromDocument?: (document: Document) => void;
  headerActionsExtra?: React.ReactNode;
}

const DocumentViewDialog: React.FC<DocumentViewDialogProps> = ({
  open,
  document,
  loading = false,
  onClose,
  onCreateFromDocument,
  headerActionsExtra
}) => {
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [exportError, setExportError] = useState('');
  const [exportMarksStatus, setExportMarksStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [exportMarksError, setExportMarksError] = useState('');

  const totalMarksCount = React.useMemo(() => {
    if (!document?.tableData) return 0;
    return document.tableData.reduce((acc, it) => acc + (it.markCodes?.length || 0), 0);
  }, [document]);

  if (!open || (!loading && !document)) return null;

  // Определяем materialID для выгрузки
  const userGuid = localStorage.getItem('guid') || '';
  const isReceiver = document?.stockIdIn?.toLowerCase() === userGuid?.toLowerCase();
  const isSender = document?.stockIdOut?.toLowerCase() === userGuid?.toLowerCase();
  const materialId = isReceiver
    ? document?.movementIdOut
    : isSender
      ? document?.movementIdIn
      : '';
  const canExport = !!(materialId && (isReceiver || isSender));
  const isReturn = document?.state === 4 || document?.type === 4;

  const handleExportInvoice = async () => {
    if (!document || !materialId) return;

    setExportStatus('loading');
    setExportError('');

    try {
      const response = await axios.post(`${API_BASE}/documents/${document.id}/export-invoice`, {
        guid: userGuid,
        materialId
      });

      if (response.data?.success === 'true' || response.data?.success === true) {
        setExportStatus('success');
        setTimeout(() => setExportStatus('idle'), 3000);
      } else {
        setExportStatus('error');
        setExportError(response.data?.text || 'Неизвестная ошибка');
        setTimeout(() => { setExportStatus('idle'); setExportError(''); }, 5000);
      }
    } catch (err: any) {
      setExportStatus('error');
      setExportError(err.response?.data?.text || 'Ошибка при обращении к серверу');
      setTimeout(() => { setExportStatus('idle'); setExportError(''); }, 5000);
    }
  };

  const handleExportMarkingCodes = async () => {
    if (!document) return;
    setExportMarksStatus('loading');
    setExportMarksError('');

    try {
      const response = await axios.get(
        `${API_BASE}/documents/${document.id}/marking-codes/export`,
        {
          params: { version: '1.0.8' },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      const docNum = getDocumentNumber(document) || document.id;
      link.download = `marking_codes_doc_${docNum}.xlsx`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setExportMarksStatus('success');
      setTimeout(() => setExportMarksStatus('idle'), 3000);
    } catch (err: any) {
      console.error('Ошибка при выгрузке КМ документа:', err);
      let errorMsg = 'Ошибка при выгрузке КМ';
      if (err.response?.data instanceof Blob) {
        try {
          const errorJson = JSON.parse(await err.response.data.text());
          errorMsg = errorJson.message || errorMsg;
        } catch (_) { }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setExportMarksStatus('error');
      setExportMarksError(errorMsg);
      setTimeout(() => {
        setExportMarksStatus('idle');
        setExportMarksError('');
      }, 5000);
    }
  };

  // Статусная история (из данных документа)
  const statusHistory = (document as any)?.statusHistory || [];

  // Коробы (из данных документа)
  const boxes = (document as any)?.boxes || [];

  // Calculate if we need to show the status column
  const hasStatusColumn = document?.tableData ? document.tableData.some(item => {
    const itemStatus = (item as any).processStatus;
    return itemStatus && itemStatus !== 'Проведен';
  }) : false;

  // Header badges: статус + тип
  const headerBadges = document ? (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: alpha(getStatusColor(document.status || 0), 0.1), px: 2, py: 0.5, borderRadius: 2 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(document.status || 0), boxShadow: `0 0 0 2px ${alpha(getStatusColor(document.status || 0), 0.3)}` }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
          {getStatusText(document.status || 0, document)}
        </Typography>
      </Box>
      <Chip
        label={document.typeName}
        size="small"
        sx={{
          borderRadius: 1,
          fontWeight: 600,
          bgcolor: colors.light,
          border: `1px solid ${colors.border}`,
          color: 'text.secondary',
          fontSize: '0.7rem'
        }}
      />
      {isReturn && (
        <Chip
          label={
            document.requestMarks === 1 || document.isMarkedProduct
              ? 'Маркированный возврат'
              : 'Возврат без маркировки'
          }
          size="small"
          color={document.requestMarks === 1 || document.isMarkedProduct ? 'primary' : 'default'}
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            fontSize: '0.7rem'
          }}
        />
      )}
    </>
  ) : null;

  // Header actions: кнопки "Создать на основании" + "Выгрузить накладную" + extra
  const headerActions = document ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {headerActionsExtra}
      {isErrorStatus(document.status || 0) && onCreateFromDocument && (
        <Button
          onClick={() => onCreateFromDocument(document)}
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          sx={{ borderColor: colors.border, color: colors.dark, textTransform: 'none' }}
        >
          Создать на основании
        </Button>
      )}
      <Button
        onClick={handleExportMarkingCodes}
        variant="outlined"
        size="small"
        disabled={exportMarksStatus === 'loading'}
        startIcon={
          exportMarksStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> :
            exportMarksStatus === 'success' ? <CheckCircle /> :
              exportMarksError ? <CloseIcon /> :
                <FileDownloadIcon />
        }
        sx={{
          textTransform: 'none',
          minWidth: exportMarksStatus === 'error' && exportMarksError ? 'auto' : undefined,
          borderColor: exportMarksStatus === 'success' ? colors.success
            : exportMarksStatus === 'error' ? colors.error
              : colors.border,
          color: exportMarksStatus === 'success' ? colors.success
            : exportMarksStatus === 'error' ? colors.error
              : colors.dark,
          bgcolor: exportMarksStatus === 'success' ? alpha(colors.success, 0.08)
            : exportMarksStatus === 'error' ? alpha(colors.error, 0.08)
              : 'transparent',
          transition: 'all 0.3s ease'
        }}
      >
        {exportMarksStatus === 'success' ? 'Выгружено!'
          : exportMarksError ? (exportMarksError || 'Ошибка')
            : exportMarksStatus === 'loading' ? 'Выгрузка...'
              : totalMarksCount > 0 ? `Выгрузка КМ'ов (${totalMarksCount})`
                : "Выгрузка КМ'ов"}
      </Button>

      <Button
        onClick={handleExportInvoice}
        variant="outlined"
        size="small"
        disabled={!canExport || exportStatus === 'loading'}
        startIcon={
          exportStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> :
            exportStatus === 'success' ? <CheckCircle /> :
              exportStatus === 'error' ? <CloseIcon /> :
                <FileUploadIcon />
        }
        sx={{
          textTransform: 'none',
          minWidth: exportStatus === 'error' && exportError ? 'auto' : undefined,
          borderColor: exportStatus === 'success' ? colors.success
            : exportStatus === 'error' ? colors.error
              : colors.border,
          color: exportStatus === 'success' ? colors.success
            : exportStatus === 'error' ? colors.error
              : colors.dark,
          bgcolor: exportStatus === 'success' ? alpha(colors.success, 0.08)
            : exportStatus === 'error' ? alpha(colors.error, 0.08)
              : 'transparent',
          transition: 'all 0.3s ease'
        }}
      >
        {exportStatus === 'success' ? 'Выгружено!'
          : exportStatus === 'error' ? (exportError || 'Ошибка')
            : exportStatus === 'loading' ? 'Выгрузка...'
              : 'Выгрузить накладную'}
      </Button>
    </Box>
  ) : null;

  // Sidebar content
  const sidebarContent = document ? (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Combined Sender/Receiver */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Отправитель</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.fromPharmacy}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Получатель</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.toPharmacy}</Typography>
            </Grid>
          </Grid>

          {/* Для возвратов: режим маркировки */}
          {isReturn && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    label="Тип возврата"
                    value={
                      document.requestMarks === 1 || document.isMarkedProduct
                        ? 'Маркированная продукция (строгий ввод КМ)'
                        : 'Без маркировки (разрешен частичный ввод)'
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ borderStyle: 'dashed' }} />

          {/* 1C Status */}
          {(document as any).processStatus && (
            <>
              <Grid container spacing={2}>
                <Grid item><Field label="Статус 1С" value={(document as any).processStatus} /></Grid>
              </Grid>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </>
          )}

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Создан / Обновлен</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(document.createdAtISO || document.createdAt)}</Typography>
            {document.updateDate && (
              <Typography variant="caption" color="text.secondary">{formatDate(document.updateDateISO || document.updateDate)}</Typography>
            )}
          </Box>
        </Box>

        {document.comment && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(colors.warning, 0.05), borderColor: alpha(colors.warning, 0.2) }}>
            <Typography variant="caption" gutterBottom sx={{ color: colors.warning, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning fontSize="inherit" /> КОММЕНТАРИЙ
            </Typography>
            <Typography variant="body2" sx={{ color: colors.dark }}>{document.comment}</Typography>
          </Paper>
        )}
      </Box>

      <Divider />

      {/* Boxes Section */}
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 fontSize="small" color="disabled" />
          Коробы ({boxes.length || document.boxCount || 0})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {boxes.length > 0 ? boxes.map((box: any) => (
            <Typography key={box.id} variant="body2" sx={{ color: colors.dark, fontSize: '0.85rem' }}>
              №{box.boxNum} - {box.barcode || ''}
            </Typography>
          )) : (
            <Typography variant="caption" color="text.secondary">
              {document.boxCount ? `${document.boxCount} шт.` : 'Нет коробов'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Delivery Status Section */}
      {document.typeMovement === 2 && document.currentPoint && (
        <>
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShippingIcon fontSize="small" color="disabled" />
              Текущее местоположение
            </Typography>
            <Typography variant="body2" sx={{ color: colors.dark, fontSize: '0.85rem' }}>
              {getStatusText(document.status || 0, document)}
            </Typography>
          </Box>
          <Divider />
        </>
      )}

      {/* History Section */}
      <Box sx={{ p: 3, flexGrow: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
          <History fontSize="small" color="disabled" />
          История статусов
        </Typography>
        <Box sx={{ position: 'relative', pl: 1 }}>
          {statusHistory.length > 0 ? (
            <>
              <Box sx={{ position: 'absolute', top: 4, bottom: 4, left: 12, width: 2, bgcolor: colors.light }} />
              {statusHistory.map((h: any, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                  <Box sx={{
                    width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                    bgcolor: idx === 0 ? getStatusColor(h.statusZid) : colors.border,
                    boxShadow: idx === 0 ? `0 0 0 2px ${alpha(getStatusColor(h.statusZid), 0.3)}` : 'none',
                    zIndex: 1
                  }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
                      {h.statusName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                      {h.recordDate} • {h.username}
                    </Typography>
                    {h.description && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: colors.light, p: 0.5, borderRadius: 1 }}>
                        {h.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(document.status || 0) }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{getStatusText(document.status || 0)}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  ) : <></>;

  return (
    <DocumentLayout
      open={open}
      onClose={onClose}
      title="ДОКУМЕНТ"
      subtitle={document ? `#${getDocumentNumber(document)}` : undefined}
      headerBadges={headerBadges}
      headerActions={headerActions}
      sidebar={sidebarContent}
      loading={loading}
    >
      {/* Main Content - Items Table */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'white', borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
          Товары ({document?.tableData?.length || 0})
        </Typography>
      </Box>

      <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${colors.border}`, overflow: 'hidden', bgcolor: 'white' }}>
          <TableContainer sx={{ maxHeight: 'calc(85vh - 140px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>№</TableCell>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>НАИМЕНОВАНИЕ</TableCell>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 100 }}>КОДЫ</TableCell>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 100 }}>КОЛ-ВО</TableCell>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 80 }}>1С</TableCell>
                  <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 140 }}>ОБНОВЛЕН</TableCell>
                  {hasStatusColumn && (
                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 250 }}>СТАТУС (ОШИБКИ)</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {document?.tableData && document.tableData.length > 0 ? (
                  document.tableData.map((row, index) => (
                    <DocumentItemRow
                      key={row.id || index}
                      row={row}
                      index={index}
                      hasStatusColumn={hasStatusColumn}
                      isReturn={isReturn}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={hasStatusColumn ? 7 : 6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Товары не найдены</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </DocumentLayout>
  );
};

// Компонент строки товара
const DocumentItemRow: React.FC<{
  row: DocumentTableRowData;
  index: number;
  hasStatusColumn: boolean;
  isReturn: boolean;
}> = ({ row, index, hasStatusColumn, isReturn }) => {
  const hasMarks = row.markCodes && row.markCodes.length > 0;
  const itemStatus = (row as any).ProcessStatus || (row as any).processStatus;

  // Парсинг серий товара (актуально для возвратов: state = 4)
  const seriesList = React.useMemo(() => {
    if (!isReturn) return [];
    const raw = (row as any).series;
    let list: any[] = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (typeof raw === 'string' && raw.trim() && raw !== '[]') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) list = parsed;
        else if (typeof parsed === 'string') list = [parsed];
      } catch {
        list = [raw];
      }
    }

    // Если в series пусто, проверяем SerialNumber / SerialCode в строке
    if (list.length === 0) {
      const singleSerial = row.SerialNumber || (row as any).SerialCode;
      if (singleSerial && String(singleSerial).trim()) {
        list = [String(singleSerial).trim()];
      }
    }

    // Парсим структуру: "Серия, СрокГодности; Сертификат"
    const counts = new Map<string, { serialNumber: string; expiryDate?: string; certificate?: string; full: string; count: number }>();

    for (const item of list) {
      const fullStr = typeof item === 'string' ? item.trim() : (item?.name || '').trim();
      if (!fullStr) continue;

      let working = fullStr;
      let certificate: string | undefined;
      const semiIdx = working.indexOf(';');
      if (semiIdx !== -1) {
        certificate = working.substring(semiIdx + 1).trim();
        working = working.substring(0, semiIdx).trim();
      }

      let serialNumber = working;
      let expiryDate: string | undefined;
      const commaIdx = working.indexOf(',');
      if (commaIdx !== -1) {
        serialNumber = working.substring(0, commaIdx).trim();
        expiryDate = working.substring(commaIdx + 1).trim();
      }

      const key = `${serialNumber}__${expiryDate || ''}`;
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { serialNumber, expiryDate, certificate, full: fullStr, count: 1 });
      }
    }

    return Array.from(counts.values());
  }, [isReturn, (row as any).series, row.SerialNumber, (row as any).SerialCode]);

  // Helper to format date
  const formatItemDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <TableRow hover sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace' }}>{index + 1}</TableCell>
      <TableCell sx={{ fontSize: '0.85rem' }}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.dark, fontWeight: 500 }}>{row.MaterialName}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span">ШК:</Box> {row.Barcode || '-'}
              <Box component="span"> • </Box>
              <Box component="span">Код:</Box> {row.MaterialCode || '-'}
            </Typography>

            {/* Серии товара для возвратов - компактно и аккуратно */}
            {isReturn && seriesList.length > 0 && (
              <>
                {/* Первые 2 серии отображаются сразу в строке */}
                {seriesList.slice(0, 2).map((s, sIdx) => (
                  <React.Fragment key={sIdx}>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
                    <Typography
                      variant="caption"
                      title={s.certificate ? `Сертификат: ${s.certificate}` : undefined}
                      sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Серия:</Box>
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
                        {s.serialNumber}
                      </Box>
                      {s.expiryDate && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                          (до {s.expiryDate})
                        </Box>
                      )}
                      {s.count > 1 && (
                        <Box component="span" sx={{ color: colors.primary, fontWeight: 600, ml: 0.5 }}>
                          [{s.count} шт]
                        </Box>
                      )}
                    </Typography>
                  </React.Fragment>
                ))}

                {/* Если уникальных серий больше 2 - аккуратный бейдж со списком по наведению */}
                {seriesList.length > 2 && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Остальные серии товара:
                          </Typography>
                          {seriesList.slice(2).map((rem, rIdx) => (
                            <Typography key={rIdx} variant="caption" sx={{ display: 'block', fontSize: '0.72rem', lineHeight: 1.4 }}>
                              • <strong>{rem.serialNumber}</strong> {rem.expiryDate ? `(до ${rem.expiryDate})` : ''} {rem.count > 1 ? `- ${rem.count} шт.` : ''}
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.primary,
                          fontWeight: 600,
                          cursor: 'pointer',
                          textDecoration: 'underline dotted',
                          '&:hover': { color: 'primary.dark' }
                        }}
                      >
                        + ещё {seriesList.length - 2} {seriesList.length - 2 === 1 ? 'серия' : (seriesList.length - 2 < 5 ? 'серии' : 'серий')}
                      </Typography>
                    </Tooltip>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        {hasMarks ? (
          <Tooltip
            title={row.markCodes?.map((m) => m.mark || '').join('\n')}
            arrow
            placement="right"
          >
            <Chip
              icon={<QrCode2 style={{ fontSize: 12 }} />}
              label={`${row.markCodes?.length} шт`}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.75rem', borderColor: colors.primary, color: colors.primary, bgcolor: alpha(colors.primary, 0.05) }}
            />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">-</Typography>
        )}
      </TableCell>
      <TableCell sx={{ fontSize: '0.85rem' }}>
        {(() => {
          const qty = parseFloat(row.Quantity) || 0;
          const qtyPcs = parseFloat(row.QuantityPcs || '0') || 0;
          const parts: string[] = [];
          if (qty > 0) parts.push(`${row.Quantity} уп`);
          if (qtyPcs > 0) parts.push(`${row.QuantityPcs} шт`);
          return parts.length > 0 ? parts.join(' + ') : '-';
        })()}
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', color: row.Quantity1C ? 'text.primary' : 'text.disabled', fontFamily: row.Quantity1C ? 'monospace' : 'inherit' }}>
        {row.Quantity1C || '-'}
      </TableCell>
      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        {formatItemDate((row as any).updateDate)}
      </TableCell>
      {hasStatusColumn && (
        <TableCell>
          {itemStatus && itemStatus !== 'Проведен' && (
            <Typography variant="caption" sx={{
              color: colors.error,
              fontWeight: 600,
              display: 'block',
              lineHeight: 1.2,
              bgcolor: alpha(colors.error, 0.05),
              p: 1,
              borderRadius: 1,
              border: `1px solid ${alpha(colors.error, 0.2)}`
            }}>
              {itemStatus}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default DocumentViewDialog;